import { ExternalLink, Info, LocateFixed, Play, X } from "lucide-react";
import type { HealthMapEdge, HealthMapNode } from "../../../shared/types";
import { useState } from "react";
import { useAuthUser } from "../../auth/AuthProvider";
import { SeverityBadge } from "../common/SeverityBadge";
import { StatusChip } from "../common/StatusChip";
import { rerunAcrJob } from "../../services/rerunService";
import { getNodeSubtitle, getRelationshipLabel } from "./healthMapUtils";

interface HealthMapDetailPanelProps {
  node?: HealthMapNode;
  connectedEdges: HealthMapEdge[];
  onClose: () => void;
  onOpenJob: (jobId: string) => void;
}

export function HealthMapDetailPanel({ node, connectedEdges, onClose, onOpenJob }: HealthMapDetailPanelProps) {
  const { hasAnyRole } = useAuthUser();
  const [actionMessage, setActionMessage] = useState<string>();
  const [isRerunning, setIsRerunning] = useState(false);
  const isAcrJobNode = Boolean(node?.relatedJobId && node.sourceType === "ACR");
  const hasRerunRole = hasAnyRole(["HealthDashboard.Operator", "HealthDashboard.Admin"]);
  const isRerunnableStatus = Boolean(node && ["FAILED", "MISSING", "LATE", "LONG_RUNNING"].includes(node.status));
  const canRerun = Boolean(isAcrJobNode && hasRerunRole && isRerunnableStatus);
  const rerunUnavailableReason = !hasRerunRole
    ? "Operator or Admin role is required. Sign out and back in if your role was recently changed."
    : node?.status === "RUNNING"
      ? "This job is already running."
      : "Rerun is available for failed, missing, late, or long-running ACR jobs.";

  async function requestRerun() {
    if (!node?.relatedJobId || isRerunning) {
      return;
    }

    const confirmed = window.confirm(`Start a new Container Apps job execution for ${node.label}?`);
    if (!confirmed) {
      return;
    }

    setIsRerunning(true);
    setActionMessage(undefined);
    try {
      const result = await rerunAcrJob(node.relatedJobId);
      setActionMessage(`Rerun requested for ${result.containerAppJobName}.`);
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : "Unable to request ACR job rerun.");
    } finally {
      setIsRerunning(false);
    }
  }

  return (
    <aside className="health-map-detail-panel" aria-label="Health map node detail">
      {node ? (
        <>
          <div className="panel-heading health-map-detail-heading">
            <div>
              <p>Node detail</p>
              <h3>{node.label}</h3>
            </div>
            <div className="health-map-detail-icons">
              <LocateFixed size={18} aria-hidden="true" />
              <button className="icon-button" type="button" aria-label="Close detail panel" onClick={onClose}>
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="node-detail-stack">
            <div className="health-map-detail-status">
              <StatusChip status={node.status} />
              <SeverityBadge severity={node.severity} />
            </div>
            <div>
              <span>Type</span>
              <strong>{getNodeSubtitle(node)}</strong>
            </div>
            <div>
              <span>Source</span>
              <strong>{node.sourceType || "PLATFORM"}</strong>
            </div>
            <div>
              <span>Owner</span>
              <strong>{node.owner || "Unassigned"}</strong>
            </div>
            {node.relatedJobId && (
              <div>
                <span>Related Job</span>
                <button className="health-map-job-link" type="button" onClick={() => onOpenJob(node.relatedJobId!)}>
                  <ExternalLink size={14} aria-hidden="true" />
                  {node.relatedJobId}
                </button>
              </div>
            )}
            <p>{node.detail}</p>
          </div>

          {isAcrJobNode && (
            <div className="health-map-action-card">
              <span>Operator action</span>
              <strong>Start a new Container Apps job execution for this ACR node.</strong>
              <button className="primary-button" type="button" disabled={!canRerun || isRerunning} onClick={requestRerun}>
                <Play size={15} aria-hidden="true" />
                {isRerunning ? "Requesting..." : "Rerun Job"}
              </button>
              <small>{actionMessage || (canRerun ? "Available for the current incident state." : rerunUnavailableReason)}</small>
            </div>
          )}

          <div className="health-map-edge-list">
            <span>Connected relationships</span>
            {connectedEdges.length ? (
              connectedEdges.slice(0, 8).map((edge) => (
                <div className="health-map-edge-row" key={edge.id}>
                  <strong>{getRelationshipLabel(edge)}</strong>
                  <small>{edge.source === node.id ? "outbound" : "inbound"}</small>
                </div>
              ))
            ) : (
              <p>No visible relationships in the current filter set.</p>
            )}
          </div>
        </>
      ) : (
        <div className="health-map-empty-detail">
          <Info size={18} aria-hidden="true" />
          <p>Select a node to inspect health, ownership, and dependency context.</p>
        </div>
      )}
    </aside>
  );
}
